import { IHeartRate } from 'fitbeat.models';

interface IMetricsCache {
  [key: string]: IHeartRate[];
}

export class InMemoryMetricsCache {
  private cache: IMetricsCache = {};
  constructor(private sampleRateInMs: number) {}

  public add(uid: string, sample: IHeartRate): boolean {
    if (sample.heartRate === 0) {
      return false;
    }

    this.initCacheItem(uid);

    const itemsToAdd = this.getItemsToAdd(uid, sample);
    const shouldAddItemToCache = itemsToAdd.length > 0;

    if (shouldAddItemToCache) {
      this.cache[uid] = this.cache[uid].concat(itemsToAdd);
    }

    return shouldAddItemToCache;
  }

  public clear(uid: string) {
    delete this.cache[uid];
  }

  public reset() {
    this.cache = {};
  }

  public get(uid: string) {
    const cache = this.cache[uid];
    return cache || [];
  }

  private getItemsToAdd(uid: string, sample: IHeartRate): IHeartRate[] {
    const lastItem = this.getLast(uid);
    if (!lastItem) {
      return [sample];
    }

    const timeSincePrevious = sample.timestamp.getTime() - lastItem.timestamp.getTime();

    if (timeSincePrevious >= this.sampleRateInMs * 2) {
      const result = this.generateItems(timeSincePrevious, lastItem, sample);
      result.push(sample);
      return result;
    } else if (timeSincePrevious >= this.sampleRateInMs) {
      return [sample];
    } else {
      return [];
    }
  }

  private generateItems(timeSincePrevious: number, previous: IHeartRate, latest: IHeartRate): IHeartRate[] {
    const itemsToGenerate = Math.floor(timeSincePrevious / this.sampleRateInMs) - 1;
    const itemsFromPreviousToLatest = itemsToGenerate + 1;     // +1 for the latest

    const heartRateChangeSincePrevious = latest.heartRate - previous.heartRate;
    const previousTimestamp = previous.timestamp.getTime();

    const timeDelta = Math.floor(timeSincePrevious / itemsFromPreviousToLatest);
    const heartRateDelta = heartRateChangeSincePrevious / itemsFromPreviousToLatest;

    const result: IHeartRate[] = [];
    for (let i = 1; i <= itemsToGenerate; ++i) {
      result.push({
        timestamp: new Date(previousTimestamp + (timeDelta * i)),
        heartRate: previous.heartRate + Math.floor(heartRateDelta * i),
        debug: 'generated in InMemoryMetricsCache.generateItems',
      });
    }
    return result;
  }

  private getLast(uid: string) {
    const cacheItem = this.cache[uid];
    return cacheItem ? cacheItem[cacheItem.length - 1] : null;
  }

  private initCacheItem(uid: string) {
    if (!this.hasItemInCache(uid)) {
      this.cache[uid] = [];
    }
  }

  private hasItemInCache(uid: string) {
    return !!this.cache[uid];
  }
}
